import { type FormEvent, useId, useState } from "react";

type Status = "idle" | "loading" | "success" | "error";

const FORM_ACTION = "https://app.loops.so/api/newsletter-form/cmhmz8vrz7lm2zt0ivb9rrd64";
const RATE_LIMIT_DURATION = 60_000;
const RATE_LIMIT_KEY = "loops-form-timestamp";

function checkRateLimit() {
  const timestamp = Date.now();
  const previousTimestamp = localStorage.getItem(RATE_LIMIT_KEY);

  if (previousTimestamp && Number(previousTimestamp) + RATE_LIMIT_DURATION > timestamp) {
    return false;
  }

  localStorage.setItem(RATE_LIMIT_KEY, timestamp.toString());
  return true;
}

export function Newsletter() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");
  const [errorMessage, setErrorMessage] = useState("Couldn’t subscribe. Please try again.");
  const emailId = useId();
  const feedbackId = useId();

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!checkRateLimit()) {
      setErrorMessage("Please wait a minute before subscribing again.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    const formBody = `userGroup=&mailingLists=&email=${encodeURIComponent(email)}`;

    try {
      const response = await fetch(FORM_ACTION, {
        method: "POST",
        body: formBody,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      if (response.ok) {
        setStatus("success");
        setEmail("");
        return;
      }

      const responseData = (await response.json()) as { message?: string };
      setErrorMessage(responseData.message || "Couldn’t subscribe. Please try again.");
      setStatus("error");
    } catch {
      setErrorMessage("Couldn’t subscribe. Check your connection and try again.");
      setStatus("error");
      localStorage.setItem(RATE_LIMIT_KEY, "");
    }
  };

  return (
    <div className="w-full max-w-[460px]">
      {status !== "success" ? (
        <form
          className="w-full"
          onSubmit={handleSubmit}
          aria-busy={status === "loading"}
        >
          <label htmlFor={emailId} className="mb-2 block text-xs" style={{ color: "rgb(var(--text-secondary))" }}>Email address</label>
          <div className="flex flex-col items-stretch gap-3 min-[400px]:flex-row">
            <input
              id={emailId}
              name="email"
              autoComplete="email"
              aria-describedby={feedbackId}
              className="m-0 box-border min-h-11 min-w-0 flex-1 rounded-lg border px-3.5 py-2.5 text-base sm:text-sm"
              style={{
                borderColor: "rgb(var(--border-color) / 0.65)",
                backgroundColor: "rgb(var(--bg-secondary) / 0.6)",
                color: "rgb(var(--text-primary))",
              }}
              placeholder="you@example.com"
              required
              type="email"
              value={email}
              onChange={event => {
                setEmail(event.target.value);
                if (status === "error") setStatus("idle");
              }}
            />
            <button
              type="submit"
              className="flex min-h-11 cursor-pointer items-center justify-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium transition-[opacity,scale] duration-150 hover:opacity-80 active:scale-[0.96] disabled:cursor-wait disabled:opacity-60"
              style={{ backgroundColor: "rgb(var(--text-primary))", color: "rgb(var(--bg-primary))" }}
              disabled={status === "loading"}
            >
              Subscribe
              {status === "loading" ? (
                <svg aria-hidden="true" className="size-3.5 animate-spin motion-reduce:animate-none" viewBox="0 0 16 16" fill="none">
                  <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" opacity="0.3" />
                  <path d="M8 2a6 6 0 0 1 6 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              ) : <span aria-hidden="true">→</span>}
            </button>
          </div>
        </form>
      ) : null}
      <p id={feedbackId} role="status" className={`mt-3 text-sm ${status === "error" ? "text-red-700 dark:text-red-400" : ""}`}>
        {status === "success" ? "You’re subscribed. Thanks for reading!" : status === "error" ? errorMessage : status === "loading" ? "Subscribing…" : ""}
      </p>
    </div>
  );
}
